import {
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

import { db } from "../firebase";

/**
 * Make sure `betaCommunityUsers/{uid}` exists and return `{ created, data }`.
 * `hint.name` is the name typed at email sign-up (Firebase hasn't saved it to
 * the account yet when this first runs).
 */
const ensureUserDoc = async (user, hint = {}) => {
  const ref = doc(db, "betaCommunityUsers", user.uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await saveAccountDetails(user);
    return { created: false, data: snap.data() };
  }

  const data = {
    name: hint.name || user.displayName || "",
    location: "",
    investingSince: "",
    tagline: "",
    bio: "",
    photoUrl: user.photoURL || "",
    avatarColor: "#DCD2F6",
    credentials: [],
    tags: [],
    followerCount: 0,
    portfolio: {
      value: 0,
      return1y: 0,
      showValue: false,
      updatedAt: null,
      holdings: [],
    },
    // Setup flags, set by the onboarding flow
    profileComplete: false,
    portfolioSetup: null,
  };

  await setDoc(ref, {
    ...data,
    createdAt: serverTimestamp(),
  });
  await saveAccountDetails(user, { created: true });

  return { created: true, data };
};

// Return the associated user profile and portfolio, or null if not found
const getUser = async (uid) => {
  const snap = await getDoc(doc(db, "betaCommunityUsers", uid));
  return snap.exists() ? { uid: snap.id, ...snap.data() } : null;
};

// Return all users; this is fine for a 20-person cohort, but paginate if 20+
const getAllUsers = async () => {
  const snap = await getDocs(collection(db, "betaCommunityUsers"));
  return snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
};

// Return the followers of the given user
const getFollowing = async (uid) => {
  const snap = await getDocs(
    collection(db, "betaCommunityUsers", uid, "following"),
  );
  return snap.docs.map((d) => d.id);
};

/**
 * Private account details at `betaCommunityUsers/{uid}/private/account`.
 * Kept out of the public profile doc because every member can read profiles;
 * Firestore rules let only the owner read/write this one (you can still see it
 * in the Firebase console). Merged on every sign-in, so it also backfills
 * members whose profile was created before this existed.
 */
const saveAccountDetails = async (user, { created = false } = {}) => {
  await setDoc(
    doc(db, "betaCommunityUsers", user.uid, "private", "account"),
    {
      email: user.email || null,
      emailVerified: Boolean(user.emailVerified),
      providers: (user.providerData || []).map((p) => p.providerId),
      lastSignInAt: serverTimestamp(),
      ...(created ? { createdAt: serverTimestamp() } : {}),
    },
    { merge: true },
  );
};

// Follow or unfollow, and modify the target's `followerCount` accordingly
const setFollow = async (uid, targetUid, shouldFollow) => {
  const batch = writeBatch(db);
  const ref = doc(db, "betaCommunityUsers", uid, "following", targetUid);
  shouldFollow ? batch.set(ref, { at: serverTimestamp() }) : batch.delete(ref);
  batch.update(
    doc(db, "betaCommunityUsers", targetUid),
    { followerCount: increment(shouldFollow ? 1 : -1) },
  );
  await batch.commit();
};

// Save profile fields (`name`, `location`, `investingSince`, `bio`, etc.)
const updateProfile = async (uid, fields) => {
  await updateDoc(doc(db, "betaCommunityUsers", uid), fields);
};

// Update the `portfolio` object; assumes `holdings` sum to 100
const updatePortfolio = async (uid, portfolio) => {
  await updateDoc(
    doc(db, "betaCommunityUsers", uid),
    { portfolio: { ...portfolio, updatedAt: serverTimestamp() } },
  );
};

export {
  ensureUserDoc,
  getAllUsers,
  getFollowing,
  getUser,
  setFollow,
  updatePortfolio,
  updateProfile,
};
